import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AppLayout } from '../../layouts/AppLayout';
import { PageHeader } from '../../layouts/PageHeader';
import { Alert } from '../../common/Alert';
import { Button } from '../../common/Button';
import { useRegionalClientPerformance } from '../../../hooks/useRegionalClientPerformance';
import { useRegionalInvoicedRevenueTrends } from '../../../hooks/useRegionalInvoicedRevenueTrends';
import {
  filterByTimePeriod,
  computeTotalMetrics,
  buildRegionSummaryRows,
  buildClientsByRegionKey,
  extractFinancialYearPeriods,
  type QuarterKey,
} from '../../../utils/regionalClientPerformanceUtils';
import { TimePeriodFilters } from './TimePeriodFilters';
import { RegionalKpiStrip } from './RegionalKpiStrip';
import { RegionSummaryTable } from './RegionSummaryTable';
import { RegionalInvoicedTrendSection } from './RegionalInvoicedTrendSection';

const SkeletonBlock: React.FC<{ className?: string }> = ({ className = 'h-24' }) => (
  <div className={`rounded-xl bg-slate-200/80 animate-pulse ${className}`} />
);

const LoadingBody: React.FC = () => (
  <div className="p-6 space-y-4 max-w-[1600px]">
    <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
      {Array.from({ length: 4 }).map((_, i) => (
        <SkeletonBlock key={i} className="h-28" />
      ))}
    </div>
    <SkeletonBlock className="h-72" />
  </div>
);

const PAGE_TITLE = 'Regional revenue & Collection';

/**
 * Regional revenue & Collection — v2 `/regional-client`.
 * Workflow: period filters (header) → KPI totals → region table (expand for clients).
 */
export const RegionalClientPerformancePage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const category = searchParams.get('category');
  const useCase = searchParams.get('useCase');

  const { records, loading, error, refetch } = useRegionalClientPerformance();
  const {
    records: trendRecords,
    loading: trendsLoading,
  } = useRegionalInvoicedRevenueTrends();
  const [selectedYear, setSelectedYear] = useState<string>('all');
  const [selectedQuarter, setSelectedQuarter] = useState<QuarterKey>('all');
  const [selectedMonth, setSelectedMonth] = useState<string>('all');
  const fyBootstrapped = useRef(false);

  useEffect(() => {
    if (fyBootstrapped.current || records.length === 0) return;
    const periods = extractFinancialYearPeriods(records);
    if (periods.length > 0) {
      setSelectedYear(periods[0]);
      fyBootstrapped.current = true;
    }
  }, [records]);

  const timeFiltered = useMemo(
    () => filterByTimePeriod(records, selectedYear, selectedQuarter, selectedMonth),
    [records, selectedYear, selectedQuarter, selectedMonth]
  );

  const regionSummaryRows = useMemo(
    () => buildRegionSummaryRows(timeFiltered),
    [timeFiltered]
  );

  const clientsByRegion = useMemo(
    () => buildClientsByRegionKey(timeFiltered),
    [timeFiltered]
  );

  const overviewMetrics = useMemo(
    () => computeTotalMetrics(timeFiltered),
    [timeFiltered]
  );

  const overviewCollPct =
    overviewMetrics.totalInvoiced > 0
      ? (overviewMetrics.totalRevenue / overviewMetrics.totalInvoiced) * 100
      : 0;

  const subtitleParts = [
    'Indian FY · USD',
    category && useCase ? `Legacy link: ${category} · ${useCase}` : null,
  ].filter(Boolean) as string[];

  if (loading) {
    return (
      <AppLayout>
        <PageHeader title={PAGE_TITLE} subtitle={subtitleParts.join(' · ')} />
        <LoadingBody />
      </AppLayout>
    );
  }

  if (error) {
    return (
      <AppLayout>
        <PageHeader title={PAGE_TITLE} />
        <div className="p-6 max-w-lg">
          <Alert type="error" title="Could not load data" message={error} />
          <Button className="mt-4" onClick={() => refetch()}>
            Retry
          </Button>
        </div>
      </AppLayout>
    );
  }

  if (records.length === 0) {
    return (
      <AppLayout>
        <PageHeader title={PAGE_TITLE} subtitle="Indian FY · USD" />
        <div className="p-6 max-w-lg">
          <Alert
            type="info"
            title="No regional rows"
            message="regional_client_performance.json loaded but is empty. Add data under public/json-data/ or public/data_models/."
          />
          <Button variant="secondary" className="mt-4" onClick={() => refetch()}>
            Reload
          </Button>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <PageHeader
        title={PAGE_TITLE}
        subtitle={subtitleParts.join(' · ')}
        filters={
          <TimePeriodFilters
            variant="toolbar"
            data={records}
            selectedYear={selectedYear}
            selectedQuarter={selectedQuarter}
            selectedMonth={selectedMonth}
            onYear={setSelectedYear}
            onQuarter={setSelectedQuarter}
            onMonth={setSelectedMonth}
            selectedRegion="all"
            regionLabel="All regions"
            recordCount={timeFiltered.length}
          />
        }
      />

      <div className="p-6 space-y-6 max-w-[1600px] pb-12">
        <RegionalKpiStrip
          mode="overview"
          totalRevenue={overviewMetrics.totalRevenue}
          collectionPct={overviewCollPct}
          totalClients={overviewMetrics.totalClients}
          totalOutstanding={overviewMetrics.totalOutstanding}
          totalInvoiced={overviewMetrics.totalInvoiced}
          regionCount={regionSummaryRows.length}
        />
        <RegionSummaryTable
          rows={regionSummaryRows}
          clientsByRegion={clientsByRegion}
        />
        <RegionalInvoicedTrendSection
          records={trendRecords}
          selectedYear={selectedYear}
          selectedQuarter={selectedQuarter}
          selectedMonth={selectedMonth}
          loading={trendsLoading}
        />
      </div>
    </AppLayout>
  );
};

export default RegionalClientPerformancePage;
